<template>
  <highcharts v-loading="loading" class="chart" :options="options" ref="highchart" />
</template>

<script>
import { mapGetters } from 'vuex';
import { isNil, cloneDeep } from 'lodash';
import moment from 'moment';
import { columnOptions } from '@/constants/chart-options';
import LMSLogAPI from '@/api/log/integrated-log';
import msgUtils from '@/utils/message';

export default {
  name: 'WLogMessageChart',
  props: {
    active: Boolean,
    compareFlag: Boolean,
    maxYAxis: Number,
  },
  data() {
    return {
      loading: false,
      selectedColumn: null,
      chartParams: null,
      maxValue: 0,
      options: cloneDeep(columnOptions),
    };
  },
  computed: mapGetters(['analysisDateParams']),
  watch: {
    maxYAxis() {
      this.options.yAxis.max = this.maxYAxis;
      this.$refs.highchart.chart.yAxis[0].update({ max: this.maxYAxis });
    },
  },
  methods: {
    /**
     * bar 차트 설정
     * @author leesy918
     * @return {[type]} [description]
     */
    settingChartOption() {
      this.options.series = [];
      this.options.lang = false;
      // 차트 Date 포멧 설정
      const startDatetime = moment(this.analysisDateParams.startDatetime);
      const endDatetime = moment(this.analysisDateParams.endDatetime);
      const diff = endDatetime.diff(startDatetime, 'hours');
      let labelFormat = '{value: %Y-%m-%d}';
      let interval = null;
      let tooltipFormat = '%Y-%m-%d %H:%M';
      if (diff < 1) {
        labelFormat = '{value: %H:%M}';
        tooltipFormat += ' %H:%M';
      } else if (diff > 0 && diff <= 24) {
        labelFormat = '{value: %H:%M}';
        interval = 300 * 1000 * 2;
      } else if (diff > 24 && diff <= 168) {
        labelFormat = '{value: %H:%M}';
        interval = 300 * 1000 * 6;
      } else if (diff > 168 && diff <= 719) {
        labelFormat = '{value: %H시}';
        interval = 300 * 1000 * 6 * 2;
      }

      this.options.xAxis = {
        type: 'datetime',
        labels: {
          format: '{value: %Y-%m-%d %H:%M}',
          style: {
            color: '$chinagray',
            fontSize: '9px',
          },
        },
        crosshair: true,
      };
      this.options.yAxis.max = this.maxYAxis;
      // bar 간격
      this.options.xAxis.tickInterval = interval;
      Object.assign(this.options.xAxis.labels, { format: labelFormat });
      this.options.tooltip = {
        xDateFormat: '%Y-%m-%d %H:%M:%S',
        split: false,
        shared: true,
      };
      // 구성 요약 출력 여부 설정
      this.options.legend = false;
      this.options.tooltip.xDateFormat = tooltipFormat;
      this.options.plotOptions.series = {
        cursor: 'pointer',
        states: {
          select: {
            color: '#fb8637',
            borderColor: '#fb8637',
          },
        },
        allowPointSelect: true,
        point: {
          events: {
            click: (e) => {
              this.getLogList(e.point.x);
            },
          },
        },
      };
      this.options.chart.height = 200;
    },
    initChart(params) {
      this.settingChartOption();
      this.chartParams = params;
      this.loadData();
      if (!this.compareFlag) {
        this.$emit('load-data', this.chartParams);
      }
    },
    /**
     * 차트 데이터 로드
     * @author leesy918
     * @return {[type]} [description]
     */
    loadData() {
      this.chartParams.interval = this.getTimeInterval(this.analysisDateParams) * 1000;
      this.loading = true;
      LMSLogAPI.getLogMessageChartData(this.chartParams).then((response) => {
        if (response.data.success) {
          const result = response.data.data;
          this.options.series = result[0].data;
          const chartData = result[0].data[0].data;
          this.maxValue = 0;
          chartData.forEach((item) => {
            if (this.maxValue < item[1]) {
              this.maxValue = item[1];
            }
          });
          this.$emit('setMaxValue', this.maxValue);
        } else {
          this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '로그 이력', false));
        }
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '로그 이력', false));
        throw new Error(e);
      }).finally(() => {
        this.loading = false;
      });
    },
    /**
     * 차트 bar 클릭 시 로그 리스트 조회
     * @author leesy918
     * @param  {[type]} startTime [description]
     * @return {[type]}           [description]
     */
    getLogList(startTime) {
      if (!isNil(this.selectedColumn) && (this.selectedColumn - startTime === 0)) {
        this.selectedColumn = null;
        this.$emit('select-column', null);
      } else {
        this.selectedColumn = startTime;
        const params = cloneDeep(this.chartParams);
        const interval = this.getTimeInterval(this.analysisDateParams) * 1000;

        params.endDateTime = startTime + interval;
        params.startDateTime = startTime;
        params.compareFlag = this.compareFlag;

        this.$emit('select-column', params);
      }
    },
    /**
     * time interval 설정 함수
     * @author leesy918
     * @param  {[type]} params [description]
     * @return {[type]}        [description]
     */
    getTimeInterval(params) {
      const statisticType = this.getStatisticType(params.startDatetime, params.endDatetime);
      switch (statisticType) {
        case 'DAY':
          return 5 * 60 * 2;
        case 'WEEK':
          return 30 * 60;
        case 'MONTH':
          return 60 * 60;
        case '2MONTH':
          return 60 * 60 * 2;
        case '3MONTH':
          return 60 * 60 * 3;
        case 'YEAR':
          return 24 * 60 * 60;
        default:
          return 60;
      }
    },
    /**
     * 시간 비교 및 설정
     * @author leesy918
     * @param  {[type]} startDateTime [description]
     * @param  {[type]} endDateTime   [description]
     * @return {[type]}               [description]
     */
    getStatisticType(startDateTime, endDateTime) {
      const gap = endDateTime - startDateTime - 1000;
      const HOURS = 1000 * 60 * 60;
      const DAY = 1000 * 60 * 60 * 24;
      const DAY19 = 1000 * 60 * 60 * 24 * 19;
      const DAY30 = 1000 * 60 * 60 * 24 * 30;
      const DAY60 = 1000 * 60 * 60 * 24 * 60;
      const DAY90 = 1000 * 60 * 60 * 24 * 90;

      if (gap >= 0 && gap <= HOURS) {
        return 'HOURS';
      } else if (gap > HOURS && gap <= DAY) {
        return 'DAY';
      } else if (gap > DAY && gap <= DAY19) {
        return 'WEEK';
      } else if (gap > DAY19 && gap <= DAY30) {
        return 'MONTH';
      } else if (gap > DAY30 && gap <= DAY60) {
        return '2MONTH';
      } else if (gap > DAY60 && gap <= DAY90) {
        return '3MONTH';
      } else if (gap > DAY90) {
        return 'YEAR';
      }
      return 'MINUTES';
    },
    closePopup() {
      this.selectedColumn = null;
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/log-analysis/components/LogMessageChart.vue